import { CalendarClock, CircleDot, Globe } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection, TagInput } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { BlogPostStatusInput, type BlogPost } from '@nucleus-commerce/api-client/admin';
import { fromZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaIdField, useMedia, MediaRichTextEditor } from '@/features/media';
import { slugify } from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  stripHtml,
  truncateWords,
  useSeoStoreContext,
} from '@/features/seo';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useBlogCategories } from '../hooks/use-blog-categories';
import {
  blogPostFormSchema,
  HANDLE,
  MAX_TAGS,
  TAG_MAX_LENGTH,
  type BlogPostFormInput,
  type BlogPostFormOutput,
} from '../schemas/blog-post-form-schema';
import { draftState, postState, type BlogPostState } from '../schemas/post-state';

export const BLOG_POST_FORM_ID = 'blog-post-form';

const NO_CATEGORY = '__none__';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'handle',
  'excerpt',
  'bodyHtml',
  'featuredMediaId',
  'authorName',
  'categoryId',
  'tags',
  'status',
  'publishedAt',
  'seo.title',
  'seo.description',
  'seo.canonical',
  'seo.noIndex',
]);
// `tags.2` from the backend lands on the tag input as a whole.
const mapField = (field: string) => (field.startsWith('tags.') ? 'tags' : field);

const STATE_ICON = { live: Globe, scheduled: CalendarClock, draft: CircleDot } as const;

export interface BlogPostFormProps {
  storeId: string;
  mode: 'create' | 'edit';
  /** The saved post in edit mode, for the rename hint, the live state and the saved SEO. */
  post: BlogPost | null;
  defaultValues: BlogPostFormInput;
  isSubmitting: boolean;
  onSubmit: (values: BlogPostFormOutput) => Promise<unknown>;
  dangerZone?: ReactNode;
}

function SavedState({ post }: { post: BlogPost }) {
  const { t } = useTranslation('blog');
  const format = useFormat();
  const state = postState(post);
  const Icon = STATE_ICON[state];
  return (
    <p className="flex items-center gap-2 text-sm" data-testid="post-saved-state">
      <Icon
        className={state === 'live' ? 'text-success size-4' : 'text-muted-foreground size-4'}
        aria-hidden
      />
      {state === 'scheduled' && post.publishedAt ? (
        <span title={format.utc(post.publishedAt)}>
          {t('savedState.scheduled', { when: format.dateTime(post.publishedAt) })}
        </span>
      ) : (
        t(`savedState.${state}`)
      )}
    </p>
  );
}

function ScheduleHint({
  state,
  publishedAt,
}: {
  state: BlogPostState;
  publishedAt: string | null;
}) {
  const { t } = useTranslation('blog');
  const format = useFormat();
  const key =
    state === 'draft'
      ? 'schedule.draft'
      : state === 'scheduled'
        ? 'schedule.future'
        : publishedAt
          ? 'schedule.past'
          : 'schedule.now';
  return (
    <p className="text-muted-foreground text-xs" role="status">
      {t(key, { when: publishedAt ? format.dateTime(publishedAt) : '' })}
    </p>
  );
}

/**
 * Two columns: content, organisation and SEO inside the <form>; publishing (status, date, save)
 * in the side rail, bound to the same react-hook-form context through `form=`.
 */
export function BlogPostForm({
  storeId,
  mode,
  post,
  defaultValues,
  isSubmitting,
  onSubmit,
  dangerZone,
}: BlogPostFormProps) {
  const { t } = useTranslation(['blog', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const form = useForm<BlogPostFormInput, unknown, BlogPostFormOutput>({
    resolver: useI18nResolver(blogPostFormSchema),
    defaultValues,
  });
  const categories = useBlogCategories(storeId);
  const storeContext = useSeoStoreContext(storeId);
  const [handleTouched, setHandleTouched] = useState(mode === 'edit');

  const values = form.watch();
  const featured = useMedia(values.featuredMediaId);
  const publishedAtIso = values.publishedAt
    ? fromZonedInput(values.publishedAt, format.timeZone)
    : null;
  const state = draftState(values.status, publishedAtIso);
  const handle = values.handle.trim();
  const renamed = mode === 'edit' && post !== null && handle !== post.handle && HANDLE.test(handle);
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const featuredAlt =
    featured.data?.alt ??
    (post?.featuredMedia?.id === values.featuredMediaId ? post.featuredMedia.alt : null);
  const live = storeContext
    ? resolveSeoPreview(
        {
          title: values.title,
          description:
            values.excerpt.trim() ||
            (values.bodyHtml ? truncateWords(stripHtml(values.bodyHtml), 300) : null),
          path: `/blog/${handle}`,
          published: state === 'live',
          override: values.seo,
          image: values.featuredMediaId ? { alt: featuredAlt } : 'none',
        },
        storeContext,
      )
    : null;

  const submit = form.handleSubmit(async (submitted) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(submitted);
      if (mode === 'edit') form.reset(submitted);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('blog:sections.publishing')} tour="content.blog-post.publishing">
              {post ? <SavedState post={post} /> : null}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('blog:fields.status')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                      >
                        {Object.values(BlogPostStatusInput).map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`blog-post-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`blog-post-status-${status}`}>
                                {t(`blog:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`blog:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="publishedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('blog:fields.publishedAt')}</FormLabel>
                    <FormControl>
                      <DateTimePicker timeZone={format.timeZone} {...field} />
                    </FormControl>
                    <FormDescription>
                      {t('blog:fields.publishedAtHint', { timeZone: format.timeZone })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <ScheduleHint state={state} publishedAt={publishedAtIso} />
              <Can permission="content.write">
                <div
                  className="flex items-center gap-2 border-t pt-4"
                  data-tour="content.blog-post.save"
                >
                  <Button
                    type="submit"
                    form={BLOG_POST_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('blog:create.submit')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('blog:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            {dangerZone}
          </>
        }
      >
        <form id={BLOG_POST_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="content.blog-post.content"
            title={t('blog:sections.content')}
            description={t('blog:sections.contentDescription')}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('blog:fields.title')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus={mode === 'create'}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!handleTouched) {
                          form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('blog:fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono text-[13px]"
                      onChange={(e) => {
                        setHandleTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      mode === 'create' ? 'blog:fields.handleHintCreate' : 'blog:fields.handleHint',
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {renamed ? (
              <Alert role="status">
                <AlertDescription>
                  {t('blog:rename.post', { from: `/blog/${post.handle}`, to: `/blog/${handle}` })}
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="excerpt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('blog:fields.excerpt')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormDescription>{t('blog:fields.excerptHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bodyHtml"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('blog:fields.body')}</FormLabel>
                  <FormControl>
                    <MediaRichTextEditor
                      value={field.value}
                      onChange={(html) => field.onChange(html ?? '')}
                      onBlur={field.onBlur}
                      aria-label={t('blog:fields.body')}
                    />
                  </FormControl>
                  <FormDescription>{t('blog:fields.bodyHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            tour="content.blog-post.featured-image"
            title={t('blog:sections.featuredMedia')}
            description={t('blog:sections.featuredMediaDescription')}
          >
            <FormField
              control={form.control}
              name="featuredMediaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('blog:fields.featuredMedia')}</FormLabel>
                  <MediaIdField
                    value={field.value}
                    onChange={(mediaId) => {
                      field.onChange(mediaId);
                      form.clearErrors('featuredMediaId');
                    }}
                    label={t('blog:fields.featuredMediaLabel')}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            tour="content.blog-post.organisation"
            title={t('blog:sections.organisation')}
            description={t('blog:sections.organisationDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="authorName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('blog:fields.author')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('blog:fields.category')}</FormLabel>
                    <Select
                      value={field.value === '' ? NO_CATEGORY : field.value}
                      onValueChange={(value) => field.onChange(value === NO_CATEGORY ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_CATEGORY}>{t('blog:fields.noCategory')}</SelectItem>
                        {(categories.data ?? []).map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.title}
                          </SelectItem>
                        ))}
                        {field.value !== '' &&
                        categories.data &&
                        !categories.data.some((c) => c.id === field.value) ? (
                          <SelectItem value={field.value}>
                            {post?.category?.id === field.value
                              ? post.category.title
                              : t('blog:fields.unknownCategory')}
                          </SelectItem>
                        ) : null}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('blog:fields.tags')}</FormLabel>
                    <FormControl>
                      <TagInput
                        value={field.value}
                        onChange={(next) => field.onChange(next.map((tag) => tag.toLowerCase()))}
                        onBlur={field.onBlur}
                        max={MAX_TAGS}
                        maxLength={TAG_MAX_LENGTH}
                        placeholder={t('blog:fields.tagsPlaceholder')}
                        removeLabel={(tag) => t('blog:fields.removeTag', { tag })}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('blog:fields.tagsHint', { max: MAX_TAGS })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

          <SeoOverrideFields />
          <SeoPreviewCard live={live} saved={post?.resolvedSeo ?? null} path={`/blog/${handle}`} />
        </form>
      </FormLayout>
    </Form>
  );
}
